import type { HandState } from './types'

/**
 * Softwise M10c/M10d — the ONE HU / multiway label, from the action log only.
 *
 * Never seeds with all table seats. The old `HU` / `N-way` pot-shape token
 * generator (and its `Pot shape · HU` rendering) was removed in M10d so
 * `헤즈업` can never appear next to `HU`.
 *
 * Pure / unit-testable.
 */
function isHeroActor(raw: string): boolean {
  const lower = raw.trim().toLowerCase()
  return (
    lower === 'hero' ||
    lower.startsWith('hero(') ||
    lower === '히어로' ||
    lower.startsWith('히어로(') ||
    lower === 'ヒーロー' ||
    lower.startsWith('ヒーロー(')
  )
}

function seatKeyForActor(
  actor: string,
  heroPosition: string,
  posByLower: Map<string, string>,
): string | null {
  const raw = actor.trim()
  if (!raw) return null
  if (isHeroActor(raw)) {
    return posByLower.get(heroPosition.toLowerCase()) ?? heroPosition
  }
  const lower = raw.toLowerCase()
  return posByLower.get(lower) ?? lower
}

/**
 * Softwise M10c — THE HU / multiway label (one place, one label).
 *
 * Players still in once preflop action closes: hero plus every seat with a
 * recorded preflop action, minus seats that folded preflop. Not-involved /
 * not-recorded seats (no recorded action) never count, table size never
 * counts, and nothing is inferred or fabricated.
 *   2 → 'hu' · 3+ → 'multiway' · ≤1 (ended preflop) → null (no label).
 * Share card, PNG and SoftGate all print this; the old pot-shape token is not
 * shown next to it.
 */
export type ShareProcessKind = 'hu' | 'multiway'

/** True when the recorded line contains at least one fold action. */
export function handHasRecordedFold(state: HandState): boolean {
  return state.actions.some((a) => a.type === 'fold')
}

/** Seats still in when preflop action closes (see ShareProcessKind). */
export function playersInAfterPreflop(
  state: HandState,
  positions: string[],
): string[] {
  const posByLower = new Map(positions.map((p) => [p.toLowerCase(), p]))
  const hero = posByLower.get(state.heroPosition.toLowerCase()) ?? state.heroPosition
  const entered = new Set<string>([hero])
  const folded = new Set<string>()
  for (const a of state.actions) {
    if (a.street !== 'preflop') continue
    const seat = seatKeyForActor(a.actor, state.heroPosition, posByLower)
    if (!seat) continue
    if (a.type === 'fold') folded.add(seat)
    else entered.add(seat)
  }
  return [...entered].filter((s) => !folded.has(s))
}

export function shareProcessKind(
  state: HandState,
  positions: string[],
): ShareProcessKind | null {
  const n = playersInAfterPreflop(state, positions).length
  if (n <= 1) return null
  return n === 2 ? 'hu' : 'multiway'
}

/** Shared Soft process-line copy for Share UI + PNG (Growth Soft-refuse). */
export function shareProcessLineText(
  kind: ShareProcessKind | null,
  ui: {
    shareProcessHu: string
    shareProcessMultiway: string
  },
): string | null {
  if (kind === 'hu') return ui.shareProcessHu
  if (kind === 'multiway') return ui.shareProcessMultiway
  return null
}
